import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { brandAccountIdLastSurveyIdMapAtom } from '~/atoms'
import {
  RedirectOrError,
  type RedirectOrErrorProps,
} from '~/components/features/RedirectOrError'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useCurrentBrandAccountSurveysQueryData } from '~/hooks/useCurrentBrandAccountSurveysQuery'
import { applyIfExist, exists, findByPath, maxBy, SurveyOp } from '~/utils'

export const BrandAccountPage = () => {
  const { t } = useTranslation()
  const currentBrandAccount = useCommonQueryCurrentBrandAccount()
  const currentBrandAccountAvailableSurveys =
    useCurrentBrandAccountSurveysQueryData()
  const [brandAccountIdLastSurveyIdMap, setBrandAccountIdLastSurveyIdMap] =
    useAtom(brandAccountIdLastSurveyIdMapAtom)

  const getRedirectPath = React.useCallback<
    RedirectOrErrorProps['getRedirectPath']
  >(() => {
    if (!exists(currentBrandAccount)) {
      return { success: false, error: t('brand_account_not_found') }
    }

    const currentBrandAccountLastSurveyId =
      brandAccountIdLastSurveyIdMap?.[currentBrandAccount.id]
    const currentBrandAccountLastSurvey = applyIfExist(
      currentBrandAccountLastSurveyId,
      (currentBrandAccountLastSurveyId) =>
        findByPath(
          currentBrandAccountAvailableSurveys,
          'id',
          currentBrandAccountLastSurveyId,
        ),
    )
    if (
      exists(currentBrandAccountLastSurvey) &&
      currentBrandAccountAvailableSurveys.includes(
        currentBrandAccountLastSurvey,
      )
    ) {
      return {
        success: true,
        path: compile('survey', { surveyId: currentBrandAccountLastSurvey.id }),
      }
    } else {
      setBrandAccountIdLastSurveyIdMap(
        applyIfExist(R.omit([currentBrandAccount.id])),
      )
    }

    const latestSurvey = maxBy(
      currentBrandAccountAvailableSurveys,
      (survey) => SurveyOp.getSurveyStartDate(survey)?.getTime() ?? -Infinity,
    )
    if (exists(latestSurvey)) {
      return {
        success: true,
        path: compile('survey', { surveyId: latestSurvey.id }),
      }
    }

    if (currentBrandAccount.isSurveyEditor) {
      return {
        success: true,
        path: compile('createSurvey', {
          brandAccountId: currentBrandAccount.id,
        }),
      }
    }

    return { success: false, error: t('survey_not_found') }
  }, [
    brandAccountIdLastSurveyIdMap,
    currentBrandAccount,
    currentBrandAccountAvailableSurveys,
    t,
    setBrandAccountIdLastSurveyIdMap,
  ])

  return <RedirectOrError getRedirectPath={getRedirectPath} />
}
